import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { MethodIcon } from '../../checkout/MethodIcon.jsx';

const EASE = [0.2, 0.7, 0.2, 1];

/**
 * HC-2: no provider name, logo or badge appears anywhere on this page.
 *
 * So the card option is labelled "Card", not by whoever is processing it, and
 * the offline options carry the merchant's own arrangements. A customer
 * choosing between these is choosing how to pay the *merchant* — which gateway
 * sits behind the card row is not information they need or would benefit from.
 *
 * The rows are radio semantics, not buttons: a customer can arrow between them,
 * and a screen reader announces "1 of 3" rather than three unrelated controls.
 */
export function MethodPicker({ methods, selected, onSelect, accent, bare = false }) {
  if (methods.length <= 1) return null;

  return (
    <div role="radiogroup" aria-label="How would you like to pay?" className={bare ? 'space-y-2' : 'mb-6 space-y-2'}>
      {/* On its own step the question is the page title, so repeating it here
          would be the same sentence twice on one screen. */}
      {!bare && <p className="eyebrow mb-2.5">How would you like to pay?</p>}

      {methods.map((method) => {
        const isSelected = method.key === selected;
        return (
          <button
            key={method.key}
            type="button"
            role="radio"
            aria-checked={isSelected}
            onClick={() => onSelect(method.key)}
            className="flex w-full items-center gap-3 rounded-xl border bg-surface px-3.5 py-3 text-left transition-colors duration-150 hover:bg-surface-2"
            style={{
              borderColor: isSelected ? accent : 'rgb(var(--line))',
              boxShadow: isSelected ? `0 0 0 3px ${accent}22` : 'none',
            }}
          >
            <span
              className="grid h-9 w-9 shrink-0 place-items-center rounded-lg transition-colors"
              style={{
                background: isSelected ? `${accent}14` : 'rgb(var(--surface-2))',
                color: isSelected ? accent : 'rgb(var(--ink-soft))',
              }}
            >
              <MethodIcon icon={method.icon} />
            </span>

            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium text-ink">{method.label}</span>
              {method.blurb && (
                <span className="mt-0.5 block text-xs leading-relaxed text-muted">{method.blurb}</span>
              )}
            </span>

            <span
              className="grid h-[18px] w-[18px] shrink-0 place-items-center rounded-full border transition-all"
              style={{
                borderColor: isSelected ? accent : 'rgb(var(--line))',
                background: isSelected ? accent : 'transparent',
              }}
            >
              {isSelected && (
                <motion.span
                  initial={{ scale: 0.4, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.16, ease: EASE }}
                >
                  <Check className="h-3 w-3 text-white" strokeWidth={3} aria-hidden="true" />
                </motion.span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default MethodPicker;
